import { IAttributeConvert } from "./IAttributeConvert";
import { FeatureType } from "@/geoMap/define/FeatureType";
import { ExportValue } from "./ExportValue";
import { AttributeValue } from "./AttributeValue";
import { AttributeView } from "./AttributeView";
import { Feature } from "ol";

/**
 * 画面サイズ（横幅）
 */
const VALUE_WIDTH = 470;

/**
 * 画面サイズ（縦幅）
 */
const VALUE_HEIGHT = 361;

/**
 * 出力JSONファイル名
 */
const VALUE_JSON_FILE_NAME = "gen_traf_yyyyMMdd_HHmmss.geojson";

/**
 * 出力CSVファイル名
 */
const VALUE_CSV_FILE_NAME = "gen_traf_yyyyMMdd_HHmmss.csv";

/**
 * タイトル
 */
const VALUE_TITLE = "一般交通量調査（箇所別基本表）";

/**
 * 一般交通量調査（箇所別基本表）属性変換クラス
 */
export class GeneralTrafficLocationAttributeConvert
  implements IAttributeConvert
{
  /**
   * 地物
   */
  feature: Feature;

  constructor(feature: Feature) {
    this.feature = feature;
  }

  /**
   * 地物タイプ取得
   */
  getFeatureType(): FeatureType {
    return FeatureType.GeneralTrafficLocation;
  }

  /**
   * 画面用フォーマットに変換
   */
  getViewFormatData(): AttributeView {
    const properties = this.feature.getProperties();
    // 画面項目定義リスト
    const items = [
      {
        key: "交通調査基本区間番号",
        value: properties["交通調査基本区間番号"],
        isSimple: true,
      },
      {
        key: "道路種別",
        value:
          properties["道路種別"] + "（" + properties["道路種別_名称"] + "）",
        isSimple: true,
      },
      { key: "路線名", value: properties["路線名"], isSimple: true },
      { key: "車線数", value: properties["車線数"], isSimple: true },
      {
        key: "昼間１２時間交通量（上下計）（合計）（台）",
        value: properties["昼間１２時間交通量（上下計）（合計）（台）"],
        isSimple: true,
      },
      {
        key: "２４時間交通量（上下計）（合計）（台）",
        value: properties["２４時間交通量（上下計）（合計）（台）"],
        isSimple: true,
      },
      {
        key: "昼間12時間大型車混入率（％）",
        value: properties["昼間12時間大型車混入率（％）"],
        isSimple: true,
      },
      { key: "混雑度", value: properties["混雑度"], isSimple: true },
      {
        key: "昼間12時間平均旅行速度（上り）",
        value: properties["昼間12時間平均旅行速度（上り）"],
        isSimple: true,
      },
      {
        key: "昼間12時間平均旅行速度（下り）",
        value: properties["昼間12時間平均旅行速度（下り）"],
        isSimple: true,
      },
      {
        key: "世代管理番号",
        value:
          properties["世代管理番号（十の位）"] !== null &&
          properties["世代管理番号（一の位）"] !== null
            ? properties["世代管理番号（十の位）"] +
              properties["世代管理番号（一の位）"]
            : null,
        isSimple: false,
      },
      {
        key: "管理区分",
        value: properties["管理区分"] + "（" + properties["管理区分"] + "）",
        isSimple: false,
      },
      {
        key: "区間延長（km）",
        value: properties["区間延長（km）"],
        isSimple: false,
      },
      {
        key: "道路状況調査単位区間番号",
        value:
          properties["道路状況調査単位区間番号（指定市コード）"] +
          " - " +
          properties["道路状況調査単位区間番号（区間番号）"],
        isSimple: false,
      },
      {
        key: "代表沿道状況",
        value:
          properties["代表沿道状況"] +
          "（" +
          properties["代表沿道状況_名称"] +
          "）",
        isSimple: false,
      },
      {
        key: "交通量調査単位区間番号",
        value:
          properties["交通量調査単位区間番号（指定市コード）"] +
          " - " +
          properties["交通量調査単位区間番号（区間番号）"],
        isSimple: false,
      },
      {
        key: "調査交通量観測・非観測の別（上り）",
        value:
          properties["調査交通量観測・非観測の別（上り）"] +
          "（" +
          properties["調査交通量観測・非観測の別（上り）_名称"] +
          "）",
        isSimple: false,
      },
      {
        key: "個別調査結果活用の別（上り）",
        value:
          properties["個別調査結果活用の別（上り）"] +
          "（" +
          properties["個別調査結果活用の別（上り）_名称"] +
          "）",
        isSimple: false,
      },
      {
        key: "12・24時間観測の別（上り）",
        value:
          properties["12・24時間観測の別（上り）"] +
          "（" +
          properties["12・24時間観測の別（上り）_名称"] +
          "）",
        isSimple: false,
      },
      {
        key: "観測地点交通調査基本区間番号（上り）",
        value: properties["観測地点交通調査基本区間番号（上り）"],
        isSimple: false,
      },
      {
        key: "観測地点名（上り）",
        value: properties["観測地点名（上り）"],
        isSimple: false,
      },
      {
        key: "昼間１２時間交通量（上り）（小型車）（台）",
        value: properties["昼間１２時間交通量（上り）（小型車）（台）"],
        isSimple: false,
      },
      {
        key: "昼間１２時間交通量（上り）（大型車）（台）",
        value: properties["昼間１２時間交通量（上り）（大型車）（台）"],
        isSimple: false,
      },
      {
        key: "昼間１２時間交通量（上り）（合計）（台）",
        value: properties["昼間１２時間交通量（上り）（合計）（台）"],
        isSimple: false,
      },
      {
        key: "２４時間交通量（上り）（小型車）（台）",
        value: properties["２４時間交通量（上り）（小型車）（台）"],
        isSimple: false,
      },
      {
        key: "２４時間交通量（上り）（大型車）（台）",
        value: properties["２４時間交通量（上り）（大型車）（台）"],
        isSimple: false,
      },
      {
        key: "２４時間交通量（上り）（合計）（台）",
        value: properties["２４時間交通量（上り）（合計）（台）"],
        isSimple: false,
      },
      {
        key: "調査交通量観測・非観測の別（下り）",
        value:
          properties["調査交通量観測・非観測の別（下り）"] +
          "（" +
          properties["調査交通量観測・非観測の別（下り）_名称"] +
          "）",
        isSimple: false,
      },
      {
        key: "個別調査結果活用の別（下り）",
        value:
          properties["個別調査結果活用の別（下り）"] +
          "（" +
          properties["個別調査結果活用の別（下り）_名称"] +
          "）",
        isSimple: false,
      },
      {
        key: "12・24時間観測の別（下り）",
        value:
          properties["12・24時間観測の別（下り）"] +
          "（" +
          properties["12・24時間観測の別（下り）_名称"] +
          "）",
        isSimple: false,
      },
      {
        key: "観測地点交通調査基本区間番号（下り）",
        value: properties["観測地点交通調査基本区間番号（下り）"],
        isSimple: false,
      },
      {
        key: "観測地点名（下り）",
        value: properties["観測地点名（下り）"],
        isSimple: false,
      },
      {
        key: "昼間１２時間交通量（下り）（小型車）（台）",
        value: properties["昼間１２時間交通量（下り）（小型車）（台）"],
        isSimple: false,
      },
      {
        key: "昼間１２時間交通量（下り）（大型車）（台）",
        value: properties["昼間１２時間交通量（下り）（大型車）（台）"],
        isSimple: false,
      },
      {
        key: "昼間１２時間交通量（下り）（合計）（台）",
        value: properties["昼間１２時間交通量（下り）（合計）（台）"],
        isSimple: false,
      },
      {
        key: "２４時間交通量（下り）（小型車）（台）",
        value: properties["２４時間交通量（下り）（小型車）（台）"],
        isSimple: false,
      },
      {
        key: "２４時間交通量（下り）（大型車）（台）",
        value: properties["２４時間交通量（下り）（大型車）（台）"],
        isSimple: false,
      },
      {
        key: "２４時間交通量（下り）（合計）（台）",
        value: properties["２４時間交通量（下り）（合計）（台）"],
        isSimple: false,
      },
      {
        key: "昼間１２時間交通量（上下計）（小型車）（台）",
        value: properties["昼間１２時間交通量（上下計）（小型車）（台）"],
        isSimple: false,
      },
      {
        key: "昼間１２時間交通量（上下計）（大型車）（台）",
        value: properties["昼間１２時間交通量（上下計）（大型車）（台）"],
        isSimple: false,
      },
      {
        key: "２４時間交通量（上下計）（小型車）（台）",
        value: properties["２４時間交通量（上下計）（小型車）（台）"],
        isSimple: false,
      },
      {
        key: "２４時間交通量（上下計）（大型車）（台）",
        value: properties["２４時間交通量（上下計）（大型車）（台）"],
        isSimple: false,
      },
      {
        key: "旅行速度調査単位区間番号",
        value:
          properties["旅行速度調査単位区間番号（指定市コード）"] +
          " - " +
          properties["旅行速度調査単位区間番号（区間番号）"],
        isSimple: false,
      },
      {
        key: "朝夕旅行速度（上り）（合計）（km/h）",
        value: properties["朝夕旅行速度（上り）（合計）（km/h）"],
        isSimple: false,
      },
      {
        key: "朝夕旅行速度（下り）（合計）（km/h）",
        value: properties["朝夕旅行速度（下り）（合計）（km/h）"],
        isSimple: false,
      },
      {
        key: "昼間旅行速度（上り）（合計）（km/h）",
        value: properties["昼間旅行速度（上り）（合計）（km/h）"],
        isSimple: false,
      },
      {
        key: "昼間旅行速度（下り）（合計）（km/h）",
        value: properties["昼間旅行速度（下り）（合計）（km/h）"],
        isSimple: false,
      },
    ];
    // 画面項目リストでAttributeValue配列を生成しクラス変数に格納後、return
    const attributeValues: AttributeValue[] = [];
    items.forEach((item) => {
      attributeValues.push(
        new AttributeValue(
          item.key,
          item.value ? item.value.toString() : null,
          item.isSimple
        )
      );
    });
    const attributeView = new AttributeView(
      attributeValues,
      VALUE_TITLE,
      VALUE_WIDTH,
      VALUE_HEIGHT,
      VALUE_HEIGHT,
      null,
      true,
      true
    );
    return attributeView;
  }

  /**
   * ファイル出力用項目取得
   */
  getExportValue(): ExportValue {
    const feature = this.feature.clone();
    feature.getGeometry().transform("EPSG:3857", "EPSG:4326");
    const attributeValues = this.getViewFormatData().attributeValues;
    return new ExportValue(
      feature,
      attributeValues,
      VALUE_JSON_FILE_NAME,
      VALUE_CSV_FILE_NAME
    );
  }
}
